import React, { useMemo, useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { storage } from '../firebase';
import { uploadBytes, getDownloadURL } from 'firebase/storage';

// ===== Seeded RNG (Mulberry32) =====
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Deterministic hash to turn a string into a 32-bit seed
function hashToSeed(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pickUniqueNumbers(count, min, max, excludeSet, rng) {
  if (count < 1) return [];
  const rangeSize = max - min + 1;
  const available = rangeSize - (excludeSet ? excludeSet.size : 0);
  if (available < count) {
    throw new Error(`Not enough unique numbers available to pick ${count} winners.`);
  }

  const winners = new Set();
  while (winners.size < count) {
    const n = min + Math.floor(rng() * rangeSize); // random int in [min, max]
    if (!excludeSet || !excludeSet.has(n)) {
      winners.add(n);
      if (excludeSet) excludeSet.add(n); // reserve globally
    }
  }
  return Array.from(winners);
}

export default function WinningNumbersGenerator({ orderId, prizes, defaultCounts, clientId, orderNumber, campaignName, isEditing }) {
  const [minNum, setMinNum] = useState(100000);
  const [maxNum, setMaxNum] = useState(999999);
  const [seedInput, setSeedInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);

// counts: default 1 each, but prize #4 (index 3) is always 0
const [counts, setCounts] = useState(() =>
  (prizes || []).map((_, idx) => (idx === 3 ? 0 : (defaultCounts?.[idx] ?? 1)))
);

useEffect(() => {
  if (!orderId) return;
  (async () => {
    try {
      const colRef = collection(db, `orders/${orderId}/winningNumberDraws`);
      const q = query(colRef, orderBy('generatedAt', 'desc'), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        const data = d.data();
        setLastResult({
          id: d.id,
          ...data,
          generatedAt: data.generatedAt?.toDate ? data.generatedAt.toDate() : new Date(),
        });
      } else {
        setLastResult(null);
      }
    } catch (e) {
      console.warn('Could not load latest winning numbers:', e);
    }
  })();
}, [orderId]);

useEffect(() => {
  const wanted = (prizes || []).length;
  setCounts(prev => {
    const next = Array.from({ length: wanted }, (_, i) =>
      i === 3 ? 0 : (prev[i] ?? (defaultCounts?.[i] ?? 1))
    );
    return next;
  });
}, [prizes, defaultCounts]);



React.useEffect(() => {
  const wanted = (prizes || []).length;
  setCounts(prev => {
    const next = Array.from({ length: wanted }, (_, i) =>
      i === 3 ? 0 : (prev[i] ?? (defaultCounts?.[i] ?? 1))
    );
    return next;
  });
}, [prizes, defaultCounts]);

const handleCountChange = (idx, val) => {
  if (idx === 3) return; // locked to 0
  const n = Math.max(0, Number(val) || 0);
  setCounts(prev => {
    const copy = [...prev];
    copy[idx] = n;
    return copy;
  });
};


  const canGenerate = useMemo(() => {
    const list = prizes || [];
    return (
      orderId &&
      list.length > 0 &&
      Number.isInteger(minNum) &&
      Number.isInteger(maxNum) &&
      maxNum > minNum &&
      counts.some(c => c > 0)
    );
  }, [orderId, prizes, minNum, maxNum, counts]);

  

    const alreadyGenerated = !!lastResult;

  const generateAndSave = async () => {
    // block when not in edit mode
    if (!isEditing) return;

    // if we already have a draw, ask for confirmation
    if (alreadyGenerated) {
      const ts = lastResult?.generatedAt
        ? new Date(lastResult.generatedAt).toLocaleString()
        : 'previously';
      const ok = window.confirm(
        `Winning numbers were already generated (${ts}). Generate a NEW set anyway?\n\n` +
        `Note: the previous set remains in history; a new CSV/message will be created.`
      );
      if (!ok) return;
    }
    if (!canGenerate) return;
    try {
      setIsSaving(true);

      // Build the seed
      const seedBasis = seedInput?.trim() ? seedInput.trim() : `${orderId}-${Date.now()}`;
      const seed = hashToSeed(seedBasis);
      const rng = mulberry32(seed);

      // ALWAYS unique across all prizes
      const globalTaken = new Set();

const items = (prizes || []).map((p, idx) => {
  const winnersCount = idx === 3 ? 0 : (counts[idx] ?? 0);
  const winners =
    winnersCount > 0 ? pickUniqueNumbers(winnersCount, minNum, maxNum, globalTaken, rng) : [];

  return {
    prizeIndex: idx,
    prizeName: p?.custom?.trim() || p?.prize || `Prize ${idx + 1}`,
    odds: p?.odds ?? '',
    winnersCount,
    winners,
  };
});




      const payload = {
        orderId,
        range: { min: minNum, max: maxNum },
        uniqueAcrossPrizes: true, // locked ON
        seedBasis,
        seed,
        generatedAt: serverTimestamp(),
        items,
      };

      const ref = collection(db, `orders/${orderId}/winningNumberDraws`);
const docRef = await addDoc(ref, payload);

// Save a summary copy on the order doc too
await updateDoc(doc(db, 'orders', orderId), {
  latestWinningNumbers: {
    ...payload,
    generatedAt: serverTimestamp(),
  },
});

// 👉 Auto-generate CSV, upload, and send internal message
try {
  // Build CSV (same as your downloadCSV function)
  const rows = [
    ['Prize Index', 'Prize Name', 'Odds', 'Winners Count', 'Winner Numbers', 'Range Min', 'Range Max', 'Seed Basis', 'Seed', 'Generated At'],
  ];
  items.forEach(item => {
    rows.push([
      item.prizeIndex,
      item.prizeName,
      item.odds,
      item.winnersCount,
      item.winners.join(' | '),
      payload.range.min,
      payload.range.max,
      payload.seedBasis,
      payload.seed,
      new Date().toISOString(),
    ]);
  });
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const csvBlob = new Blob([csv], { type: 'text/csv' });

  // Upload to Storage
  const ts = Date.now();
  const filename = `winning_numbers_${orderId}_${ts}.csv`;
  const fullPath = `messages/${clientId}/${orderId}/${filename}`;
  const fileRef = ref(storage, fullPath);
  await uploadBytes(fileRef, csvBlob);
  const fileUrl = await getDownloadURL(fileRef);

  // Post internal message
  if (clientId) {
    const msgCol = collection(db, 'clients', clientId, 'messages');
    const textLine =
      `📊 Winning numbers generated for Job #${orderNumber || orderId}${campaignName ? ` (${campaignName})` : ''}\n` +
      `Range ${payload.range.min}–${payload.range.max} · Seed: ${payload.seedBasis}`;

    await addDoc(msgCol, {
      sender: 'system',
      senderName: 'System',
      senderRole: 'system',
      recipientType: 'internal',
      text: textLine,
      createdAt: serverTimestamp(),
      read: false,
      clientId,
      orderId,
      orderNumber: orderNumber || '',
      campaignName: campaignName || '',
      isOrderMessage: true,
      attachmentUrl: fileUrl,
      attachmentName: filename,
      attachmentType: 'text/csv',
      attachmentPath: fullPath,
    });
  }
} catch (e) {
  console.warn('CSV upload or message post failed:', e);
}

// update UI
const resultForUI = { ...payload, id: docRef.id, generatedAt: new Date() };
setLastResult(resultForUI);

alert('Winning numbers generated, saved, and sent to internal messages.');

    } finally {
      setIsSaving(false);
    }
  };

  const downloadCSV = () => {
    if (!lastResult?.items?.length) return;
    const rows = [
      ['Prize Index', 'Prize Name', 'Odds', 'Winners Count', 'Winner Numbers', 'Range Min', 'Range Max', 'Seed Basis', 'Seed', 'Generated At'],
    ];
    lastResult.items.forEach(item => {
      rows.push([
        item.prizeIndex,
        item.prizeName,
        item.odds,
        item.winnersCount,
        item.winners.join(' | '),
        lastResult.range.min,
        lastResult.range.max,
        lastResult.seedBasis,
        lastResult.seed,
        (lastResult.generatedAt || new Date()).toISOString(),
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = `winning_numbers_${orderId}.csv`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

const generateDisabled = !isEditing || !canGenerate || isSaving;

return (
  <div className="wng-card">
    <h3 className="wng-title">Winning Numbers Generator</h3>

          {alreadyGenerated && (
        <div className="wng-alert">
          A set of winning numbers already exists. Generating again will create a new set.
        </div>
      )}

    <div className="wng-grid">
      <div className="wng-field">
        <label className="wng-label">Range Min</label>
        <input
          className="wng-input"
          type="number"
          value={minNum}
          onChange={e => setMinNum(parseInt(e.target.value || '0', 10))}
          placeholder="e.g., 100000"
        />
      </div>

      <div className="wng-field">
        <label className="wng-label">Range Max</label>
        <input
          className="wng-input"
          type="number"
          value={maxNum}
          onChange={e => setMaxNum(parseInt(e.target.value || '0', 10))}
          placeholder="e.g., 999999"
        />
      </div>

      <div className="wng-field wng-seed">
        <label className="wng-label">
          Seed <span className="wng-hint">(optional)</span>
        </label>
        <input
          className="wng-input"
          type="text"
          value={seedInput}
          onChange={e => setSeedInput(e.target.value)}
          placeholder="Leave blank to auto-generate"
        />
        <div className="wng-help">Enter any word/number to make the draw reproducible.</div>
      </div>
    </div>

    <div className="wng-subtitle">Winners per Prize</div>

    <div className="wng-prize-list">
      {(prizes || []).map((p, idx) => {
        const item = lastResult?.items?.find(it => it.prizeIndex === idx);
        const winners = item?.winners ?? [];
        const count = idx === 3 ? 0 : (counts[idx] ?? 1);

        return (
          <div key={idx} className="wng-prize-row">
            <div className="wng-prize-info">
              <div className="wng-prize-name">{p?.custom?.trim() || p?.prize || `Prize ${idx + 1}`}</div>

              {/* Inline winners if generated */}
              {lastResult && (
                <div className="wng-inline-winners">
                  <span className="wng-inline-winners-label">
                    Winners ({item?.winnersCount ?? 0}):
                  </span>{' '}
                  <span className="wng-inline-winners-list">
                    {winners.length ? winners.join(', ') : '-'}
                  </span>
                </div>
              )}
            </div>

            <div className="wng-prize-count">
              <label className="wng-label">Count</label>
              <input
                className="wng-input wng-input-small"
                type="number"
                min={0}
                value={count}
                onChange={e => handleCountChange(idx, e.target.value)}
                disabled={idx === 3}
                title={
                  idx === 3
                    ? 'This prize is for all others; no winners are drawn'
                    : 'Set number of winners for this prize'
                }
              />
            </div>

          </div>
        );
      })}
    </div>

      <div className="wng-actions">
        <button
          className="wng-btn wng-btn-primary"
          onClick={generateAndSave}
          disabled={generateDisabled}
          title={
            !isEditing
              ? 'Switch to Edit mode to generate'
              : (!canGenerate ? 'Fill in required fields to generate' : '')
          }
        >
          {isSaving ? 'Saving…' : alreadyGenerated ? 'Regenerate & Save' : 'Generate & Save'}
        </button>

        <button
          className="wng-btn wng-btn-secondary"
          onClick={downloadCSV}
          disabled={!lastResult}
          title={!lastResult ? 'Generate first to enable download' : ''}
        >
          Download CSV
        </button>
      </div>
    </div>
  );
}
